/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ProjectImageModel } from '@app/models/project-image.model';
import { ProjectImagesListService } from '@app/services/api-services/project-images/project-images-list.service';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class ProjectImageService extends ApiBaseService {
  private _projectImage: ProjectImageModel;
  protected _endpoint = 'project_images';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private projectImagesListService: ProjectImagesListService
  ) {
    super(http, authUserService);
  }

  get projectImage(): ProjectImageModel {
    return this._projectImage;
  }

  set projectImage(value: ProjectImageModel) {
    this._projectImage = value;
  }

  /**
   *  Warren Dowey
   *
   *  getProjectImageFromListById
   *
   * : Get a ProjectImageModel based on the projectImageUuid by checking the list
   *
   * @param projectImageUuid of type string
   *
   * @returns ProjectImageModel: gets the projectImage with the projectImageUuid and that meets the query
   */
  getProjectImageFromListById(projectImageUuid: string): void {
    this.projectImage = this.projectImagesListService.getProjectImageById(projectImageUuid);
    if (this.projectImage) {
      this.dataSubject.next(this.projectImage);
    } else {
      this.getProjectImageById(projectImageUuid);
    }
  }

  /**
   *  Warren Dowey
   *
   *  getProjectImageFromListById
   *
   * : Get a ProjectImageModel based on the projectImageUuid by checking the list
   *
   * @param projectImageUuid of type string
   *
   * @returns ProjectImageModel: gets the projectImage with the projectImageUuid and that meets the query
   */
  getProjectImageFromListBySlug(slug: string): void {
    const images = this.projectImagesListService.getProjectImagesByProjectSlug(slug);
    if (images.length) {
      this.dataSubject.next(images);
    } else {
      this.getProjectImageBySlug(slug);
      this.projectImagesListService.getData({});
    }
  }
  /**
   *
   *
   * @param {string} projectImageUuid
   * @memberof ProjectImageService
   */
  getProjectImageById(projectImageUuid: string): void {
    const apiParamsModel = new ApiParamsModel();
    apiParamsModel.filter = [
      { field: 'project_image_uuid', operator: 'eq', value: projectImageUuid },
    ];
    this.getData(apiParamsModel);
  }
  /**
   *
   *
   * @param {string} slug
   * @memberof ProjectImageService
   */
  getProjectImageBySlug(slug: string): void {
    const apiParamsModel = new ApiParamsModel();
    apiParamsModel.filter = [{ field: 'project_slug', operator: 'eq', value: slug }];
    this.getData(apiParamsModel);
  }
  /**
   * Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectImageModel
   *
   * @returns void
   */
  protected handleData(data: ProjectImageModel[]): void {
    if (data) {
      this.projectImage = data[0];
    }
    super.handleData(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleInsert
   *
   * : handles the insert of a new projectImage into the list
   *
   * @param data of type ProjectImageModel
   *
   * @returns void
   */
  protected handleInsert(data: ProjectImageModel[]): void {
    if (data) {
      this.projectImage = data[0];
      this.projectImagesListService.addToList(data[0]);
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleUpdate
   *
   * : handles the update of a projectImage in the list
   *
   * @param data of type ProjectImageModel
   *
   * @returns void
   */
  protected handleUpdate(data: ProjectImageModel[]): void {
    if (data) {
      this.projectImage = data[0];
      this.projectImagesListService.updateInList(data[0]);
    }
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleDelete
   *
   * : deletes an projectApp from the list
   *
   * @param data of type projectImageModel
   *
   * @returns void
   */
  protected handleDelete(data: ProjectImageModel[]): void {
    this.deleteSubject.next(data);
  }
}
